import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import StancerPaymentModal from '../hubup/StancerPaymentModal';
import {
  confirmCrmMailPackPayment,
  createCrmMailPackCheckout,
  fetchCrmMailPackOffer,
  quoteCrmMailPack,
} from '../../api/crmMailPacks';

function formatEur(value, { suffix = true } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const formatted = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
  return suffix ? `${formatted} TTC` : formatted;
}

function formatEmails(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('fr-FR').format(num);
}

function htDetail(htEur, vatPercent) {
  const ht = Number(htEur);
  if (!Number.isFinite(ht) || ht <= 0) return null;
  const vat = Number(vatPercent) || 20;
  return `${new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(ht)} HT · TVA ${vat} %`;
}

function RecapPriceRow({ label, detail, amountEur }) {
  return (
    <div className="flex justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-foreground leading-snug">{label}</p>
        {detail ? (
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{detail}</p>
        ) : null}
      </div>
      <span className="text-sm font-medium text-foreground shrink-0 tabular-nums">
        {formatEur(amountEur, { suffix: false })}
      </span>
    </div>
  );
}

export default function CrmMailPackOffersModal({
  open,
  onClose,
  onPurchaseComplete,
  initialSuccess = false,
  initialError = null,
}) {
  const [step, setStep] = useState('choose');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offer, setOffer] = useState(null);
  const [quote, setQuote] = useState(null);
  const [affiliateInput, setAffiliateInput] = useState('');
  const [appliedAffiliateCode, setAppliedAffiliateCode] = useState('');
  const [affiliateApplying, setAffiliateApplying] = useState(false);
  const [affiliateError, setAffiliateError] = useState(null);
  const [stancerPayment, setStancerPayment] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const refreshQuote = useCallback(async (code) => {
    const res = await quoteCrmMailPack(code);
    if (res?.success === false) {
      throw new Error(res?.data?.message || 'Total invalide.');
    }
    return res?.data ?? null;
  }, []);

  const loadOffer = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchCrmMailPackOffer();
      setOffer(res?.data?.offer ?? null);
    } catch (e) {
      setError(e?.message || 'Impossible de charger l’offre.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (initialSuccess) {
      setStep('success');
      setError(null);
      return;
    }
    setStep('choose');
    setError(initialError);
    setQuote(null);
    setAffiliateInput('');
    setAppliedAffiliateCode('');
    setAffiliateError(null);
    setStancerPayment(null);
    void loadOffer();
  }, [open, initialSuccess, initialError, loadOffer]);

  const goToSummary = async () => {
    setLoading(true);
    setError(null);
    setAffiliateError(null);
    setAffiliateInput('');
    setAppliedAffiliateCode('');
    try {
      const q = await refreshQuote('');
      if (!q) throw new Error('Impossible de calculer le total.');
      setQuote(q);
      setStep('summary');
    } catch (e) {
      setError(e?.message || 'Impossible d’afficher le récapitulatif.');
    } finally {
      setLoading(false);
    }
  };

  const applyAffiliateCode = async (codeOverride) => {
    const code =
      codeOverride !== undefined ? String(codeOverride).trim() : affiliateInput.trim();
    setAffiliateApplying(true);
    setAffiliateError(null);
    try {
      const q = await refreshQuote(code || undefined);
      if (!q) throw new Error('Impossible de calculer le total.');
      if (code && !q.affiliateCode) {
        throw new Error('Code promo invalide.');
      }
      setQuote(q);
      setAppliedAffiliateCode(q.affiliateCode || '');
      setAffiliateInput(q.affiliateCode || code);
    } catch (e) {
      setAffiliateError(e?.message || 'Code promo invalide.');
      setAppliedAffiliateCode('');
      try {
        const q = await refreshQuote('');
        setQuote(q);
      } catch {
        /* keep previous quote */
      }
    } finally {
      setAffiliateApplying(false);
    }
  };

  const handleCheckout = async () => {
    const pendingCode = affiliateInput.trim();
    if (pendingCode && pendingCode !== appliedAffiliateCode) {
      setAffiliateError('Cliquez sur « Appliquer » pour valider le code promo.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}?crmMailPack=return`;
      const res = await createCrmMailPackCheckout({
        returnUrl,
        affiliateCode: appliedAffiliateCode || undefined,
      });
      const data = res?.data ?? {};
      if (!data.paymentUrl || !data.paymentId) {
        throw new Error('Paiement indisponible.');
      }
      setStancerPayment({
        paymentId: data.paymentId,
        paymentUrl: data.paymentUrl,
      });
    } catch (e) {
      setError(e?.message || 'Impossible de démarrer le paiement.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentAuthorized = async (paymentId) => {
    setConfirmingPayment(true);
    setError(null);
    try {
      await confirmCrmMailPackPayment(paymentId);
      setStancerPayment(null);
      setStep('success');
      onPurchaseComplete?.();
    } catch (e) {
      setError(e?.message || 'Paiement non confirmé.');
    } finally {
      setConfirmingPayment(false);
    }
  };

  const baseTotalTtc =
    quote?.totalBeforeTtcEur ?? offer?.priceTtcEur ?? 0;
  const summaryTotal =
    quote?.totalPriceTtcEur ?? offer?.priceTtcEur ?? 0;
  const affiliateSavings =
    appliedAffiliateCode && baseTotalTtc > summaryTotal
      ? baseTotalTtc - summaryTotal
      : 0;
  const affiliateApplied = Boolean(appliedAffiliateCode);

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="crm-mail-pack-title"
    >
      <div className="relative w-full max-w-lg rounded-xl border border-border bg-card shadow-elevation-3 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
          <h2 id="crm-mail-pack-title" className="text-lg font-semibold text-foreground">
            Recharger les e-mails CRM
          </h2>
          <button
            type="button"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label="Fermer"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === 'success' ? (
            <div className="text-center space-y-3 py-4">
              <Icon name="CheckCircle2" size={40} className="mx-auto text-success" />
              <p className="text-sm font-medium text-foreground">
                Quota e-mail rechargé avec succès.
              </p>
              <Button type="button" onClick={onClose}>
                Fermer
              </Button>
            </div>
          ) : confirmingPayment ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Validation du paiement…
            </p>
          ) : step === 'summary' ? (
            <>
              <p className="text-sm font-medium text-foreground">Récapitulatif de commande</p>

              {offer ? (
                <div className="rounded-lg border border-border p-4 space-y-3 text-sm">
                  <div className="space-y-3 pb-3 border-b border-border">
                    <RecapPriceRow
                      label={offer.label || `${formatEmails(offer.emails)} e-mails`}
                      detail={htDetail(offer.priceHtEur, offer.vatRatePercent)}
                      amountEur={offer.priceTtcEur}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Sous-total TTC</span>
                      <span
                        className={
                          affiliateApplied && affiliateSavings > 0
                            ? 'text-muted-foreground line-through shrink-0 tabular-nums'
                            : 'font-medium text-foreground shrink-0 tabular-nums'
                        }
                      >
                        {formatEur(baseTotalTtc, { suffix: false })}
                      </span>
                    </div>

                    {affiliateApplied && affiliateSavings > 0 ? (
                      <div className="flex justify-between text-xs text-success">
                        <span>Code promo « {appliedAffiliateCode} »</span>
                        <span className="font-semibold tabular-nums">
                          −{formatEur(affiliateSavings, { suffix: false })}
                        </span>
                      </div>
                    ) : null}

                    <div className="flex justify-between pt-2 border-t border-border">
                      <span className="font-medium text-foreground">Total TTC</span>
                      <span className="font-semibold text-foreground text-lg tabular-nums">
                        {formatEur(summaryTotal, { suffix: false })}
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="rounded-lg border border-border p-4 space-y-2">
                <label
                  htmlFor="crm-promo-code"
                  className="text-xs font-medium text-foreground block"
                >
                  Code promo
                </label>
                <div className="flex gap-2">
                  <input
                    id="crm-promo-code"
                    type="text"
                    className={`flex-1 min-w-0 rounded-md border bg-background px-3 py-2 text-sm uppercase font-mono focus:outline-none focus:ring-2 focus:ring-ring ${
                      affiliateError
                        ? 'border-error'
                        : affiliateApplied
                          ? 'border-success'
                          : 'border-input'
                    }`}
                    value={affiliateInput}
                    onChange={(e) => {
                      setAffiliateInput(e.target.value.toUpperCase());
                      setAffiliateError(null);
                    }}
                    placeholder="Entrez votre code"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    disabled={affiliateApplying || !affiliateInput.trim()}
                    onClick={() => void applyAffiliateCode()}
                  >
                    {affiliateApplying ? '…' : 'Appliquer'}
                  </Button>
                </div>
                {affiliateApplied ? (
                  <p className="text-xs font-medium text-success">
                    Code promo « {appliedAffiliateCode} » appliqué.
                  </p>
                ) : null}
                {affiliateError ? (
                  <p className="text-xs font-medium text-error" role="alert">
                    {affiliateError}
                  </p>
                ) : null}
                {appliedAffiliateCode ? (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline hover:text-foreground"
                    onClick={() => {
                      setAffiliateInput('');
                      void applyAffiliateCode('');
                    }}
                  >
                    Retirer le code promo
                  </button>
                ) : null}
              </div>

              <p className="text-xs text-muted-foreground leading-relaxed">
                Crédit ajouté à l&apos;entreprise active. Chaque e-mail envoyé depuis le CRM
                consomme 1 crédit.
              </p>

              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={loading}
                  onClick={() => {
                    setStep('choose');
                    setAffiliateError(null);
                  }}
                >
                  Retour
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  disabled={loading || confirmingPayment || Boolean(stancerPayment)}
                  onClick={() => void handleCheckout()}
                >
                  {loading ? 'Préparation…' : 'Payer par carte'}
                </Button>
              </div>
            </>
          ) : (
            <>
              {loading && !offer ? (
                <p className="text-sm text-muted-foreground text-center py-6">Chargement…</p>
              ) : offer ? (
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-foreground">
                        {formatEmails(offer.emails)} e-mails
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {htDetail(offer.priceHtEur, offer.vatRatePercent)} ·{' '}
                        {formatEur(offer.priceTtcEur)}
                      </p>
                    </div>
                    <p className="text-lg font-semibold text-foreground shrink-0">
                      {formatEur(offer.priceTtcEur)}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Recharge du quota e-mail de votre entreprise CRM.
                  </p>
                </div>
              ) : null}

              {error ? (
                <p className="text-sm text-error" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
                  Annuler
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  disabled={loading || !offer}
                  onClick={() => void goToSummary()}
                >
                  {loading ? 'Chargement…' : 'Continuer'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modal, document.body)}
      {stancerPayment ? (
        <StancerPaymentModal
          iframeUrl={stancerPayment.paymentUrl}
          paymentId={stancerPayment.paymentId}
          onAuthorized={handlePaymentAuthorized}
          onError={(msg) => setError(msg)}
          onClose={() => setStancerPayment(null)}
        />
      ) : null}
    </>
  );
}

export async function confirmCrmMailPackReturnFromUrl(searchParams) {
  if (searchParams.get('crmMailPack') !== 'return') {
    return { ok: false, reason: 'skip' };
  }
  const paymentId = searchParams.get('paymentId') || searchParams.get('payment');
  if (!paymentId) {
    return { ok: false, reason: 'error', message: 'Paiement introuvable dans l’URL de retour.' };
  }
  try {
    await confirmCrmMailPackPayment(paymentId);
    return { ok: true, paymentId };
  } catch (e) {
    return {
      ok: false,
      reason: 'error',
      message: e?.message || 'Impossible de confirmer le paiement.',
    };
  }
}
